<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { router, useForm } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import Button from '@/Components/Utils/Button.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import CountUp from 'vue-countup-v3'
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import { notify } from '@kyvg/vue3-notification'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faAddressBook, faSkull, faMoneyBillWave, faPlus, faMinus, faPlusCircle, faTag, faShoppingCart, faPencil, faTrash } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
import Modal from '@/Components/Utils/Modal.vue'
library.add(faAddressBook, faSkull, faMoneyBillWave, faPlus, faMinus, faPlusCircle, faTag, faShoppingCart, faPencil, faTrash)

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    data: {
        id: number
        slug: string
        name: string
        phone: string | null
        address: string | null
        deposit_amount: number
        status: string
        is_inactive: boolean
        created_at: string
        deposit_histories: {
            id: number
            created_at: string
            created_by: string | null
            amount_before: number
            amount_change: number
            amount_after: number
            type: string
            type_label: string
            note: string | null
        }[]
        product_prices: {
            id: number
            product_id: number
            price: number
            product: { id: number; name: string; slug: string } | null
        }[]
        recent_orders: {
            id: number
            slug: string
            reference: string
            product_name: string | null
            quantity: number
            state_label: string
            state_icon: { icon: string; class: string; tooltip: string }
            created_at: string
        }[]
    }
    stats: {
        orders_count: number
    }
    all_flexo_products: {
        id: number
        name: string
        slug: string
        price: number
    }[]
}>()

// -- Adjust Deposit Dialog
const isDepositOpen = ref(false)
const depositMode = ref<'top_up' | 'deduct'>('top_up')
const depositAmount = ref(0)
const depositNote = ref('')
const isSubmitting = ref(false)

const typeOptions = [
    { label: 'Top Up', value: 'top_up' },
    { label: 'Pembayaran Invoice', value: 'payment' },
    { label: 'Refund', value: 'refund' },
    { label: 'Penyesuaian Manual', value: 'manual' },
]
const selectedType = ref('top_up')

const openDeposit = (mode: 'top_up' | 'deduct') => {
    depositMode.value = mode
    selectedType.value = mode === 'top_up' ? 'top_up' : 'manual'
    depositAmount.value = 0
    depositNote.value = ''
    isDepositOpen.value = true
}

// Section: Flexo product prices
const isOpenAddProductPrice = ref(false)
const isDeletingPrice = ref(false)
const isDeletePriceModalOpen = ref(false)
const isEditingPrice = ref(false)
const editPriceValue = ref<number | null>(null)
const newProductPriceForm = useForm({
    price: null as number | null,
})
const isLoadingSubmitProductPrice = ref(false)

const openEditPrice = () => {
    editPriceValue.value = props.data.product_prices[0]?.price ?? null
    isEditingPrice.value = true
}
const onSubmitProductPrice = () => {
    const price = isEditingPrice.value ? editPriceValue.value : newProductPriceForm.price
    if (!price) {
        notify({ title: 'Error', text: 'Masukkan harga khusus', type: 'error' })
        return
    }
    const product = props.all_flexo_products[0]
    if (!product) return
    router.post(
        route('casheer.products.customer_price.store', product.id),
        { customer_id: props.data.id, price },
        {
            preserveScroll: true,
            only: ['data'],
            onStart: () => {
                isLoadingSubmitProductPrice.value = true
            },
            onSuccess: () => {
                notify({ title: 'Berhasil!', text: 'Harga khusus berhasil disimpan.', type: 'success' })
                isOpenAddProductPrice.value = false
                isEditingPrice.value = false
                editPriceValue.value = null
                newProductPriceForm.reset()
            },
            onError: () => {
                notify({ title: 'Gagal', text: 'Gagal menyimpan harga khusus.', type: 'error' })
            },
            onFinish: () => {
                isLoadingSubmitProductPrice.value = false
            },
        }
    )
}

const onDeleteProductPrice = (productId: number) => {
    isDeletingPrice.value = true
    router.delete(
        route('casheer.products.customer_price.delete', productId),
        {
            data: { customer_id: props.data.id },
            preserveScroll: true,
            only: ['data'],
            onSuccess: () => {
                notify({ title: 'Berhasil!', text: 'Harga khusus berhasil dihapus.', type: 'success' })
                isDeletePriceModalOpen.value = false
            },
            onError: () => {
                notify({ title: 'Gagal', text: 'Gagal menghapus harga khusus.', type: 'error' })
            },
            onFinish: () => { isDeletingPrice.value = false },
        }
    )
}

const submitDeposit = () => {
    if (!depositAmount.value || depositAmount.value <= 0) {
        notify({ title: 'Error', text: 'Masukkan nominal deposit yang valid', type: 'error' })
        return
    }
    isSubmitting.value = true

    const amountChange = depositMode.value === 'top_up' ? depositAmount.value : -depositAmount.value

    router.post(
        route('casheer.customers.adjust_deposit', [props.data.id]),
        { amount_change: amountChange, type: selectedType.value, note: depositNote.value || undefined },
        {
            onSuccess: () => {
                isDepositOpen.value = false
                notify({ title: 'Berhasil', text: 'Deposit berhasil diperbarui', type: 'success' })
            },
            onError: () => {
                notify({ title: 'Oops!', text: 'Gagal memperbarui deposit', type: 'error' })
            },
            onFinish: () => { isSubmitting.value = false },
        }
    )
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #button-delete-customer="{ action }">
            <div>
                <ModalConfirmationDelete
                    :routeDelete="action.route"
                    isFullLoading
                >
                    <template #default="{ isOpenModal, changeModel }">
                        <Button
                            @click="() => changeModel()"
                            :style="action.style"
                            :label="action.label"
                            :icon="action.icon"
                            :key="`ActionButton${action.style}`"
                            :tooltip="action.tooltip"
                        />
                    </template>
                </ModalConfirmationDelete>
            </div>
        </template>
    </PageHeading>

    <div class="px-4 mt-4 grid md:grid-cols-2 gap-6 max-w-4xl">
        <!-- Left: Customer info card -->
        <div>
            <div class="w-full overflow-hidden rounded-xl border border-sky-400">
                <div class="flex flex-col gap-y-1 border-b border-gray-900/5 bg-gray-50 p-6">
                    <div class="font-bold text-lg">
                        <FontAwesomeIcon icon="fal fa-address-book" class="mr-2 text-sky-500" fixed-width aria-hidden="true" />
                        {{ data.name }}
                    </div>
                    <div v-if="data.is_inactive" class="flex items-center gap-1 text-sm text-red-500">
                        <FontAwesomeIcon icon="fal fa-skull" fixed-width aria-hidden="true" />
                        Nonaktif
                    </div>
                    <div class="text-sm text-gray-400 italic">{{ useFormatTime(data.created_at) }}</div>
                </div>

                <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">No. HP</dt>
                        <dd class="font-medium">{{ data.phone || '-' }}</dd>
                    </div>
                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">Alamat</dt>
                        <dd class="font-medium text-right max-w-xs">{{ data.address || '-' }}</dd>
                    </div>
                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">Deposit</dt>
                        <dd :class="data.deposit_amount > 0 ? 'font-medium text-green-600' : data.deposit_amount < 0 ? 'font-medium text-red-600' : 'text-gray-400'">
                            {{ useFormatCurrency(data.deposit_amount) }}
                        </dd>
                    </div>
                </dl>

                <!-- Deposit action buttons -->
                <div class="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end">
                    <button
                        @click="openDeposit('deduct')"
                        class="flex items-center gap-2 px-3 py-1.5 rounded bg-red-50 border border-red-300 text-red-700 text-sm hover:bg-red-100 transition"
                    >
                        <FontAwesomeIcon icon="fal fa-minus" fixed-width aria-hidden="true" />
                        Kurangi
                    </button>
                    <button
                        @click="openDeposit('top_up')"
                        class="flex items-center gap-2 px-3 py-1.5 rounded bg-green-50 border border-green-300 text-green-700 text-sm hover:bg-green-100 transition"
                    >
                        <FontAwesomeIcon icon="fal fa-plus" fixed-width aria-hidden="true" />
                        Top Up
                    </button>
                </div>
            </div>
        </div>

        <!-- Right: Flexo Special Price card -->
        <div v-if="all_flexo_products.length > 0">
            <div class="w-full overflow-hidden rounded-xl border border-blue-200">
                <div class="flex items-center gap-2 border-b border-gray-900/5 bg-gray-50 px-5 py-4">
                    <FontAwesomeIcon icon="fal fa-tag" class="text-blue-400" fixed-width aria-hidden="true" />
                    <span class="font-semibold text-gray-700">Harga Khusus Flexo</span>
                </div>

                <div class="px-5 py-4 space-y-3 text-sm">
                    <div class="flex justify-between text-gray-500">
                        <span>Produk</span>
                        <Link :href="route('casheer.products.show', all_flexo_products[0].slug)" class="font-medium text-sky-600 hover:underline">
                            {{ all_flexo_products[0].name }}
                        </Link>
                    </div>
                    <div class="flex justify-between text-gray-500">
                        <span>Harga Normal</span>
                        <span class="font-medium text-gray-700">{{ useFormatCurrency(all_flexo_products[0].price) }}</span>
                    </div>
                    <div class="flex justify-between items-center">
                        <span class="text-gray-500">Harga Khusus</span>
                        <span v-if="!isEditingPrice">
                            <span v-if="data.product_prices.length > 0" class="font-semibold text-blue-700">{{ useFormatCurrency(data.product_prices[0].price) }}</span>
                            <span v-else class="text-gray-400 italic text-xs">Belum diset</span>
                        </span>
                        <InputNumber
                            v-else
                            v-model="editPriceValue"
                            :min="0"
                            mode="currency"
                            currency="IDR"
                            locale="id-ID"
                            :maxFractionDigits="0"
                            inputClass="!w-40 text-right"
                            autofocus
                            size="small"
                        />
                    </div>
                </div>

                <!-- Actions -->
                <div class="px-5 py-3 border-t border-gray-100">
                    <!-- No price yet -->
                    <template v-if="data.product_prices.length === 0 && !isEditingPrice">
                        <button
                            @click="isOpenAddProductPrice = true"
                            class="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
                        >
                            <FontAwesomeIcon icon="fal fa-plus-circle" fixed-width aria-hidden="true" />
                            Set Harga Khusus
                        </button>
                    </template>

                    <!-- Has price, not editing -->
                    <template v-else-if="data.product_prices.length > 0 && !isEditingPrice">
                        <div class="flex items-center gap-4 justify-end">
                            <button @click="isDeletePriceModalOpen = true" class="flex items-center gap-1 text-xs text-red-400 hover:text-red-600">
                                <FontAwesomeIcon icon="fal fa-trash-alt" fixed-width />
                                Hapus
                            </button>
                            <button @click="openEditPrice" class="flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800">
                                <FontAwesomeIcon icon="fal fa-pencil" fixed-width />
                                Edit
                            </button>
                        </div>
                    </template>

                    <!-- Editing mode -->
                    <template v-else-if="isEditingPrice">
                        <div class="flex items-center gap-4 justify-end">
                            <button @click="isEditingPrice = false" :disabled="isLoadingSubmitProductPrice" class="text-xs text-gray-400 hover:text-gray-600">Batal</button>
                            <button @click="onSubmitProductPrice" :disabled="isLoadingSubmitProductPrice" class="text-xs px-3 py-1 rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50">{{ isLoadingSubmitProductPrice ? 'Menyimpan...' : 'Simpan' }}</button>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>

    <!-- Deposit History Table -->
    <div class="px-4 mt-8 max-w-4xl">
        <div class="flex items-center gap-2 mb-3">
            <FontAwesomeIcon icon="fal fa-money-bill-wave" class="text-sky-500" fixed-width aria-hidden="true" />
            <h3 class="text-base font-semibold text-gray-700">Riwayat Deposit</h3>
        </div>
        <div v-if="data.deposit_histories.length === 0" class="text-sm text-gray-400 italic py-4">
            Belum ada riwayat deposit.
        </div>
        <div v-else class="overflow-hidden rounded-xl border border-gray-200">
            <table class="w-full text-sm">
                <thead class="bg-gray-50 text-gray-500">
                    <tr>
                        <th class="px-4 py-3 text-left font-medium">Tanggal</th>
                        <th class="px-4 py-3 text-left font-medium">Tipe</th>
                        <th class="px-4 py-3 text-right font-medium">Sebelum</th>
                        <th class="px-4 py-3 text-right font-medium">Perubahan</th>
                        <th class="px-4 py-3 text-right font-medium">Sesudah</th>
                        <th class="px-4 py-3 text-left font-medium">Catatan</th>
                        <th class="px-4 py-3 text-left font-medium">Oleh</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    <tr v-for="h in data.deposit_histories" :key="h.id" class="hover:bg-gray-50">
                        <td class="px-4 py-3 text-gray-600 whitespace-nowrap">{{ useFormatTime(h.created_at) }}</td>
                        <td class="px-4 py-3">
                            <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                :class="h.amount_change > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                            >
                                {{ h.type_label }}
                            </span>
                        </td>
                        <td class="px-4 py-3 text-right text-gray-500">{{ useFormatCurrency(h.amount_before) }}</td>
                        <td class="px-4 py-3 text-right font-medium"
                            :class="h.amount_change > 0 ? 'text-green-600' : 'text-red-600'"
                        >
                            {{ h.amount_change > 0 ? '+' : '' }}{{ useFormatCurrency(h.amount_change) }}
                        </td>
                        <td class="px-4 py-3 text-right font-medium text-gray-800">{{ useFormatCurrency(h.amount_after) }}</td>
                        <td class="px-4 py-3 text-gray-500 max-w-xs truncate">{{ h.note || '-' }}</td>
                        <td class="px-4 py-3 text-gray-500">{{ h.created_by || '-' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- Recent Orders -->
    <div class="px-4 mt-8 max-w-4xl">
        <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
                <FontAwesomeIcon icon="fal fa-shopping-cart" class="text-sky-500" fixed-width aria-hidden="true" />
                <h3 class="text-base font-semibold text-gray-700">Riwayat Order</h3>
            </div>
            <Link
                v-if="stats.orders_count > 5"
                :href="route('casheer.orders.index') + '?filter[global]=' + encodeURIComponent(data.name)"
                class="text-sm text-sky-500 hover:text-sky-700 hover:underline"
            >
                Lihat lainnya ({{ stats.orders_count }})
            </Link>
        </div>
        <div v-if="!data.recent_orders.length" class="text-sm text-gray-400 italic py-4">
            Belum ada order.
        </div>
        <div v-else class="overflow-hidden rounded-xl border border-gray-200">
            <table class="w-full text-sm">
                <thead class="bg-gray-50 text-gray-500">
                    <tr>
                        <th class="px-4 py-3 text-left font-medium">Tanggal</th>
                        <th class="px-4 py-3 text-left font-medium">No. Order</th>
                        <th class="px-4 py-3 text-left font-medium">Produk</th>
                        <th class="px-4 py-3 text-right font-medium">Qty</th>
                        <th class="px-4 py-3 text-left font-medium">Status</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                    <tr v-for="order in data.recent_orders" :key="order.id" class="hover:bg-gray-50">
                        <td class="px-4 py-3 text-gray-500 whitespace-nowrap">{{ useFormatTime(order.created_at) }}</td>
                        <td class="px-4 py-3">
                            <Link :href="route('casheer.orders.show', order.slug)" class="primaryLink font-medium">
                                {{ order.reference }}
                            </Link>
                        </td>
                        <td class="px-4 py-3 text-gray-700">{{ order.product_name || '-' }}</td>
                        <td class="px-4 py-3 text-right text-gray-700">{{ order.quantity }}</td>
                        <td class="px-4 py-3">
                            <span
                                v-tooltip="order.state_icon?.tooltip"
                                class="inline-flex items-center gap-1 text-xs"
                                :class="order.state_icon?.class"
                            >
                                <FontAwesomeIcon v-if="order.state_icon?.icon" :icon="order.state_icon.icon" fixed-width />
                                {{ order.state_label }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- Confirm Delete Price Modal -->
    <Dialog
        v-model:visible="isDeletePriceModalOpen"
        header="Hapus Harga Khusus"
        modal
        :style="{ width: '380px' }"
        :draggable="false"
    >
        <div class="flex flex-col gap-2 py-2">
            <p class="text-sm text-gray-700">Yakin ingin menghapus harga khusus untuk pelanggan <span class="font-semibold">{{ data.name }}</span>?</p>
            <p class="text-sm text-gray-500">Akan berlaku di order selanjutnya.</p>
        </div>
        <template #footer>
            <button
                @click="isDeletePriceModalOpen = false"
                :disabled="isDeletingPrice"
                class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >Batal</button>
            <button
                @click="onDeleteProductPrice(data.product_prices[0].product_id)"
                :disabled="isDeletingPrice"
                class="px-5 py-2 rounded text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition disabled:opacity-50"
            >{{ isDeletingPrice ? 'Menghapus...' : 'Ya, Hapus' }}</button>
        </template>
    </Dialog>

    <!-- Adjust Deposit Dialog -->
    <Dialog
        v-model:visible="isDepositOpen"
        :header="depositMode === 'top_up' ? 'Top Up Deposit' : 'Kurangi Deposit'"
        modal
        :style="{ width: '420px' }"
        :draggable="false"
    >
        <div class="flex flex-col gap-4 pt-2">
            <div>
                <label class="text-sm font-medium text-gray-700 mb-1 block">Nominal</label>
                <InputNumber
                    v-model="depositAmount"
                    :min="1"
                    mode="currency"
                    currency="IDR"
                    locale="id-ID"
                    :prefix="depositMode === 'top_up' ? '' : '-'"
                    :maxFractionDigits="0"
                    class="w-full"
                    placeholder="Masukkan nominal"
                />
            </div>
            <div>
                <label class="text-sm font-medium text-gray-700 mb-1 block">Tipe</label>
                <Select
                    v-model="selectedType"
                    :options="depositMode === 'top_up'
                        ? typeOptions.filter(o => ['top_up','manual','refund'].includes(o.value))
                        : typeOptions.filter(o => ['payment','manual'].includes(o.value))"
                    optionLabel="label"
                    optionValue="value"
                    class="w-full"
                />
            </div>
            <div>
                <label class="text-sm font-medium text-gray-700 mb-1 block">Catatan <span class="text-gray-400">(opsional)</span></label>
                <Textarea
                    v-model="depositNote"
                    rows="2"
                    class="w-full"
                    placeholder="Catatan tambahan..."
                />
            </div>
        </div>
        <template #footer>
            <button
                @click="isDepositOpen = false"
                class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >Batal</button>
            <button
                @click="submitDeposit"
                :disabled="isSubmitting"
                class="px-5 py-2 rounded text-sm font-medium text-white transition"
                :class="depositMode === 'top_up' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'"
            >
                {{ isSubmitting ? 'Memproses...' : (depositMode === 'top_up' ? 'Top Up' : 'Kurangi') }}
            </button>
        </template>
    </Dialog>

    <!-- Modal: Set Harga Khusus Produk Flexo -->
    <Modal :isOpen="isOpenAddProductPrice" @onClose="() => { isOpenAddProductPrice = false; newProductPriceForm.reset() }" width="w-full max-w-lg" closeButton>
        <div class="bg-white px-6 py-6 sm:rounded-lg">
            <div class="text-center text-xl font-medium mb-6">Set Harga Khusus Produk Flexo</div>

            <div class="space-y-5">
                <div v-if="all_flexo_products.length > 0" class="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-sm">
                    <span class="text-gray-500">Produk:</span>
                    <span class="ml-1 font-medium text-gray-800">{{ all_flexo_products[0].name }}</span>
                    <span class="ml-3 text-gray-400">Harga normal: <span class="font-medium">{{ useFormatCurrency(all_flexo_products[0].price) }}</span></span>
                </div>

                <div>
                    <label class="text-sm font-medium text-gray-700 mb-1 block">Harga Khusus</label>
                    <InputNumber
                        v-model="newProductPriceForm.price"
                        :min="0"
                        mode="currency"
                        currency="IDR"
                        locale="id-ID"
                        :maxFractionDigits="0"
                        class="w-full"
                        placeholder="Masukkan harga khusus"
                    />
                </div>

                <Button
                    style="secondary"
                    label="Simpan"
                    full
                    icon="fad fa-save"
                    :loading="isLoadingSubmitProductPrice"
                    @click="onSubmitProductPrice"
                />
            </div>
        </div>
    </Modal>
</template>

